import { withDependencies, optional, named } from '@wix/thunderbolt-ioc'
import { ViewModeSym, BrowserWindowSymbol, PageFeatureConfigSymbol } from '@wix/thunderbolt-symbols'
import type { AnimationData, MenuContainerCompFactory, MenuContainerData, MenuContainerDriver } from './types'
import { name } from './symbols'
import { Animations } from 'feature-animations'
import { SiteScrollBlockerSymbol } from 'feature-site-scroll-blocker'
import { disableCyclicTabbing, enableCyclicTabbing, isSSR } from '@wix/thunderbolt-commons'
import { AnimationHandler } from './AnimationHandler'
import { ComponentsStoreSymbol } from 'feature-components'
import _ from 'lodash'

const menuContainerDriverFactory: MenuContainerCompFactory = (
	{ componentsConfig },
	viewMode,
	siteScrollBlocker,
	animations,
	window
) => {
	const animationHandler = animations && AnimationHandler(animations)

	return {
		componentType: 'MenuContainer',
		getComponentDriver: (viewerComponent) => {
			const onToggleCallbacks: Record<string, (isOpen: boolean) => void> = {}
			const menuIds = Object.keys(componentsConfig)

			const animateIn = (animation: AnimationData) =>
				new Promise<void>((resolve) => {
					animationHandler!.animate(
						viewerComponent.id,
						{
							inBehavior: animation,
							outBehavior: { name: 'outBehavior' },
						},
						true,
						() => {
							resolve()
						}
					)
				})

			const toggle = (immediate: boolean = false) =>
				new Promise<void>((resolve) => {
					const isOpen = !viewerComponent.getProps().isOpen
					const shouldAnimate = !immediate && animationHandler
					const shouldUpdateVisibility = isOpen || !shouldAnimate
					const isMobile = viewMode === 'mobile'
					if (isOpen) {
						enableCyclicTabbing(menuIds.map((id) => `#${id}`))
					} else {
						disableCyclicTabbing()
					}

					siteScrollBlocker && siteScrollBlocker.setSiteScrollingBlocked(isOpen, viewerComponent.id)
					if (shouldAnimate) {
						animationHandler.animate(
							viewerComponent.id,
							(componentsConfig[viewerComponent.id] as MenuContainerData).animations,
							isOpen,
							(reversed: boolean) => {
								const isClosed = reversed ? isOpen : !isOpen
								if (isClosed) {
									viewerComponent.updateProps({
										isVisible: false,
									})
								}
								resolve()
							}
						)
					}

					Object.values(onToggleCallbacks).forEach((c) => c(isOpen))
					viewerComponent.updateProps({
						isOpen,
						...(shouldUpdateVisibility && { isVisible: isOpen }),
					})
					if (isOpen) {
						viewerComponent.updateStyle({
							'--menu-height':
								!isSSR(window) && isMobile ? window.getComputedStyle(document.body).height : '100vh',
						})
					}
					if (!shouldAnimate) {
						resolve()
					}
				})

			const toggleIfNeeded = async (openMenu: boolean, immediate?: boolean) => {
				const isMenuOpen = viewerComponent.getProps().isOpen

				if (openMenu !== isMenuOpen) {
					await toggle(immediate)
				}
			}

			const api: MenuContainerDriver = {
				open: (immediate) => toggleIfNeeded(true, immediate),
				toggle,
				onToggle: (callback) => {
					const callbackId = _.uniqueId('callback')
					onToggleCallbacks[callbackId] = callback
					return () => delete onToggleCallbacks[callbackId]
				},
				close: (immediate) => toggleIfNeeded(false, immediate),
				animateIn,
				unblockScroll: () =>
					siteScrollBlocker && siteScrollBlocker.setSiteScrollingBlocked(false, viewerComponent.id),
			}

			return api as any
		},
	}
}

export const MenuContainerDriverFactory = withDependencies(
	[
		named(PageFeatureConfigSymbol, name),
		ViewModeSym,
		optional(SiteScrollBlockerSymbol),
		optional(Animations),
		BrowserWindowSymbol,
		ComponentsStoreSymbol,
	],
	menuContainerDriverFactory
)
